import { NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import logo61 from '../assets/img/logo61-marca.png';
import '../assets/css/Header.css';

/** Colunas do mega-menu, na ordem em que aparecem. Grupo sem item visível some. */
const GRUPOS_SERVICOS = ['Operação', 'Gestão', 'Administração'];

function Header() {
  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const page = header.closest('.page');
    if (!page) return undefined;
    // Inclui a borda e a segunda linha da navegação em telas menores.
    const medir = () => page.style.setProperty('--app-header-height', `${header.getBoundingClientRect().height}px`);
    medir();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    observer?.observe(header);
    window.addEventListener('resize', medir);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', medir);
      page.style.removeProperty('--app-header-height');
    };
  }, []);

  const navigate = useNavigate();
  const { isLogado, isAdmin, isAdministrador, isDiretor, isAssistente, permissao, nomeUsuario, idCorretor, logout } = useAuth();
  const profileRef = useRef(null);
  const servicosRef = useRef(null);
  const gestaoRef = useRef(null);

  const [menuAberto, setMenuAberto] = useState(false);
  const [servicosAberto, setServicosAberto] = useState(false);
  const [gestaoAberto, setGestaoAberto] = useState(false);

  const iniciais = useMemo(() => {
    const partes = String(nomeUsuario).trim().split(' ').filter(Boolean);
    if (partes.length === 0) return 'U';
    if (partes.length === 1) return partes[0].slice(0, 1).toUpperCase();
    return `${partes[0][0]}${partes[1][0]}`.toUpperCase();
  }, [nomeUsuario]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleTrocarSenha = () => {
    setMenuAberto(false);
    navigate('/TrocarSenha');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setMenuAberto(false);
      }

      if (servicosRef.current && !servicosRef.current.contains(event.target)) {
        setServicosAberto(false);
      }

      if (gestaoRef.current && !gestaoRef.current.contains(event.target)) {
        setGestaoAberto(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setMenuAberto(false);
        setServicosAberto(false);
        setGestaoAberto(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const getNavLinkClass = ({ isActive }) =>
    isActive ? 'nav-link active-link' : 'nav-link';

  const atalhos = [
    { to: '/Parcerias', label: 'Parcerias', end: false, show: isLogado },
    {
      href: 'https://sites.google.com/view/portal-do-corretor-61-imoveis/seja-bem-vindo',
      label: 'Portal do Corretor',
      external: true,
      show: isLogado,
    },
    { to: '/61Financiamento', label: '61Financeiro', end: false, show: true },
    // { to: '/Experts', label: 'Experts', end: false, show: true },
  ].filter((item) => item.show && !isAssistente);

  // `grupo` define a coluna no mega-menu. Eram 20 itens numa lista só — em seção,
  // a pessoa varre pelo tipo de trabalho em vez de ler tudo.
  const servicos = [
    { to: '/', label: 'Início', end: true, grupo: 'Operação', show: true },
    { to: '/NovaVisita', label: 'Criar Visita', grupo: 'Operação', show: isLogado },
    { to: '/JornadaCaptacao?novo=1', label: 'Novo Imóvel', subtitle: 'captação', grupo: 'Operação', show: isLogado },
    { to: '/JornadaCaptacao', label: 'Jornada Captação', grupo: 'Operação', show: isLogado },
    { to: '/AppVisita', label: 'Corretor', grupo: 'Operação', show: isLogado },
    { to: '/Tarefas', label: 'Minhas Tarefas', subtitle: 'pendências de todos os módulos', grupo: 'Operação', show: isLogado && isAdmin},
    { to: '/GestaoLeads', label: 'Gestão de Leads', subtitle: 'ao vivo do C2S', grupo: 'Gestão', show: isLogado && isAdmin },
    { to: '/GestaoClientes', label: 'Gestao de Clientes', grupo: 'Gestão', show: isLogado },
    { to: '/GestaoVisitas', label: 'Gestão de Visitas', subtitle: 'revisão e conversão', grupo: 'Gestão', show: isLogado && isAdmin },
    // Relatorio Gerente saiu do menu em 25/08/2026: visitas, imoveis visitados,
    // clientes, ranking, evolucao e os tres PDFs consolidados passaram para a Gestao
    // de Visitas; leads e propostas para os modulos deles. A ROTA continua viva para
    // quem tiver link salvo — so a entrada saiu.
    // { to: '/RelatorioGerente', label: 'Relatório Gerente', show: isLogado && isAdmin },
    { to: '/VisaoDiretor', label: 'Visão do Diretor', grupo: 'Gestão', show: isLogado && (isDiretor || permissao === 'gerente') },
    { to: '/ConsultaImoveis', label: 'Gestão de Imóveis', subtitle: 'estoque, saídas e foco', grupo: 'Gestão', show: isLogado && (isDiretor || isAdministrador || isAssistente || permissao === 'gerente') },
    { to: '/PropostasEfetivas', label: 'Gestão de Propostas', subtitle: 'propostas de compra', grupo: 'Gestão', show: isLogado && (isDiretor || isAdministrador || isAssistente || permissao === 'gerente') },
    { to: '/ranking', label: 'Ranking', grupo: 'Administração', show: isLogado && isAdmin },
    { to: '/AdminBases', label: 'Gestão de Bases', grupo: 'Administração', show: isLogado && isAdministrador },
    { to: '/Vendas', label: 'Vendas', grupo: 'Administração', show: isLogado && (isAdministrador || isDiretor) },
    { to: '/Solicitacoes', label: 'Solicitações', subtitle: 'pedidos e resultados', grupo: 'Administração', show: isLogado && (isAssistente || isAdministrador) },
    { to: '/LancarImovel', label: 'Lançar Imóvel', subtitle: 'Imoview + Trello', grupo: 'Administração', show: isLogado && (isAssistente || isAdministrador) },
    // Assistente (perfil do estagiário) só enxerga Lançar Imóvel e o acompanhamento das propostas.
  ].filter((item) => item.show).filter((item) => !isAssistente || ['/Solicitacoes', '/LancarImovel', '/PropostasEfetivas', '/ConsultaImoveis'].includes(item.to));

  const gestao = [
    { to: '/GerenteRH', label: 'Controle Equipe', show: isLogado && (permissao === 'gerente' || isAdministrador) },
    { to: '/GerenciarEquipes', label: 'Equipes', show: isLogado && isAdministrador },
    { to: '/RHUsuarios', label: 'RH Usuarios', show: isLogado && isAdministrador },
    { to: '/ControleCorretor', label: 'Controle usuarios', show: isLogado && isAdministrador },
    { to: '/register', label: 'Registrar Usuario', show: isLogado && (isAdministrador || isAssistente) },
  ].filter((item) => item.show).filter((item) => !isAssistente || item.to === '/register');

  return (
    <header className="app-header" ref={headerRef}>
      {/* Trilho interno: prende o conteúdo a 1280px e centraliza. Sem ele, a barra
          espalha os itens até as bordas da tela em monitor largo. */}
      <div className="header-inner">
      <div className="brand-area" onClick={() => navigate('/')}>
        {/* Era um ícone de casa genérico. A marca da 61 é o ativo que identifica o
            produto — casa genérica é o que qualquer imobiliária poderia usar.
            `alt` vazio + aria-hidden porque "Imobiliária 61" vem escrito ao lado:
            anunciar a imagem faria o leitor de tela repetir o nome. */}
        <img src={logo61} className="brand-logo" alt="" aria-hidden="true" />

        {/* Era `<h1>` e repetia em toda página do app — o h1 pertence ao conteúdo
            da página, não ao cabeçalho global. */}
        <div className="brand-text">
          <span className="brand-kicker">Inteligência</span>
          <span className="brand-name">Imobiliária 61</span>
        </div>
      </div>

      {atalhos.length > 0 && (
        <nav className="atalhos-bar" aria-label="Atalhos">
          {atalhos.map((item) =>
            item.external ? (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link nav-atalho"
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  isActive ? 'nav-link nav-atalho active-link' : 'nav-link nav-atalho'
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>
      )}

      <div className="header-right">
        <nav className="main-nav">
          <div
            className={`services-dropdown ${servicosAberto ? 'open' : ''}`}
            ref={servicosRef}
          >
            <button
              type="button"
              className="services-button"
              onClick={() => setServicosAberto((prev) => !prev)}
              aria-expanded={servicosAberto}
              aria-label="Abrir menu de serviços"
            >
              Serviços
              <span className={`services-chevron ${servicosAberto ? 'open' : ''}`}>
                ▾
              </span>
            </button>

            {servicosAberto && (
              <div className="services-menu services-menu--mega">
                {GRUPOS_SERVICOS.map((grupo) => {
                  const itens = servicos.filter((item) => item.grupo === grupo);
                  if (itens.length === 0) return null;
                  return (
                    <div className="services-col" key={grupo}>
                      <span className="services-col-title">{grupo}</span>
                      {itens.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.end}
                          className={getNavLinkClass}
                          onClick={() => setServicosAberto(false)}
                        >
                          {item.label}
                          {item.subtitle && (
                            <span className="services-menu-subtitle">{item.subtitle}</span>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {gestao.length > 0 && (
            <div
              className={`services-dropdown ${gestaoAberto ? 'open' : ''}`}
              ref={gestaoRef}
            >
              <button
                type="button"
                className="services-button"
                onClick={() => setGestaoAberto((prev) => !prev)}
                aria-expanded={gestaoAberto}
                aria-label="Abrir menu de gestão"
              >
                Gestão
                <span className={`services-chevron ${gestaoAberto ? 'open' : ''}`}>
                  ▾
                </span>
              </button>

              {gestaoAberto && (
                <div className="services-menu">
                  {gestao.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={getNavLinkClass}
                      onClick={() => setGestaoAberto(false)}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isLogado && (
            <NavLink to="/login" className={getNavLinkClass}>
              Login
            </NavLink>
          )}
        </nav>

        {isLogado && (
          <div className="profile-wrapper" ref={profileRef}>
            <button
              type="button"
              className={`profile-button ${menuAberto ? 'open' : ''}`}
              onClick={() => setMenuAberto((prev) => !prev)}
              aria-label="Abrir menu do usuário"
              aria-expanded={menuAberto}
            >
              <span className="profile-avatar">{iniciais}</span>
            </button>

            {menuAberto && (
              <div className="profile-dropdown">
                <div className="profile-dropdown-header">
                  <div className="profile-dropdown-avatar">{iniciais}</div>
                  <div className="profile-dropdown-user">
                    <strong>{nomeUsuario}</strong>
                    <span>ID: {idCorretor}</span>
                  </div>
                </div>

                <button onClick={handleLogout} className="logout-button">
                  Sair
                </button>
                <button className="profile-menu-button" onClick={() => {
                  setMenuAberto(false); navigate('/MeuPerfil');
                }}>Meus dados</button>
                <button onClick={handleTrocarSenha} className="profile-menu-button">
                  Trocar senha
                </button>
                {isAdministrador && <button className="profile-menu-button" onClick={() => {
                  setMenuAberto(false); navigate('/RecuperarSenha');
                }}>Gerar senha temporária</button>}
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </header>
  );
}

export default Header;
